<!--
  -  TTTech nerve-management-system
  -  Copyright(c) 2021. TTTech Industrial Automation AG.
  -
  -  ALL RIGHTS RESERVED.
  -
  -  Usage of this software, including source code, netlists, documentation,
  -  is subject to restrictions and conditions of the applicable license
  -  agreement with TTTech Industrial Automation AG or its affiliates.
  -
  -  All trademarks used are the property of their respective owners.
  -
  -  TTTech Industrial Automation AG and its affiliates do not assume any liability
  -  arising out of the application or use of any product described or shown
  -  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
  -  make changes, at any time, in order to improve reliability, function or
  -  design.
  -
  -  Contact Information:
  -  support@tttech-industrial.com
  -
  -  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
  -
  -->

<template>
  <NerveDialog
    v-model="dialogState"
    scrollable
    persistent
    max-width="500"
    @keydown.esc="close()"
    @keydown.enter="confirm()"
  >
    <v-card id="iiotConfirmDialogCard" class="confirm-dialog d-flex flex-column">
      <v-card-title class="text-center">
        {{ $t(utilsStore.confirmDialogParams.title) }}
      </v-card-title>
      <v-card-text v-if="utilsStore.confirmDialogParams.subTitle" class="flex-grow-1 text-center text-grey-darken-1">
        <div>
          {{ $t(utilsStore.confirmDialogParams.subTitle) }}
        </div>
        <div v-if="utilsStore.confirmDialogParams.subTitleSecondLine">
          {{ $t(utilsStore.confirmDialogParams.subTitleSecondLine) }}
        </div>
        <v-checkbox
          v-if="utilsStore.confirmDialogParams.isCheckboxVisible"
          id="confirmDialogDontRemoveImagesCheckbox"
          v-model="utilsStore.confirmDialogParams.isChecked"
          data-cy="confirmDialogDontRemoveImagesCheckbox"
          :label="$t('nodes.deployedWorkloadControl.confirmMessage')"
          class="ml-4"
          @change="updateDontRemoveImages()"
        />
      </v-card-text>

      <v-card-actions>
        <v-checkbox
          v-if="utilsStore.confirmDialogParams.dontAskMeAgainCallback"
          id="confirmDialogDontShowAgainCheckbox"
          v-model="utilsStore.confirmDialogParams.dontAskMeAgain"
          :label="$t('dialog.doNotShowDialogAgainMessage')"
          class="mr-auto"
        />
        <NerveButton
          id="confirmDialogCancelButton"
          :text="$t('dialog.cancel')"
          type-of-btn="close"
          size="normal"
          class="my-0"
          autofocus
          @click-event="close()"
        />
        <NerveButton
          id="confirmDialogYesButton"
          :text="$t('dialog.yes')"
          type-of-btn="action"
          size="normal"
          class="my-0"
          @click-event="confirm()"
        />
      </v-card-actions>
    </v-card>
  </NerveDialog>
</template>

<script setup>
import { computed } from 'vue';
import { NerveButton, NerveDialog } from '@nerve/ui-components';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';

/*
  Stores
*/
const utilsStore = useUtilsStore();

/*
  Computed
*/
const dialogState = computed({
  get() {
    return utilsStore.confirmDialog;
  },
  set() {
    close();
  },
});

/*
  Methods
*/
const close = async () => {
  if (utilsStore.confirmDialogParams.cancelCallback) {
    await utilsStore.confirmDialogParams.cancelCallback();
  }
  utilsStore.closeConfirmDialog();
};

const confirm = async () => {
  if (utilsStore.confirmDialogParams.dontAskMeAgain && utilsStore.confirmDialogParams.dontAskMeAgainCallback) {
    utilsStore.confirmDialogParams.dontAskMeAgain = false;
    await utilsStore.confirmDialogParams.dontAskMeAgainCallback();
  }
  await utilsStore.confirmDialogParams.callback();
  utilsStore.closeConfirmDialog();
};

const updateDontRemoveImages = () => {
  utilsStore.showConfirmDialog(utilsStore.confirmDialogParams);
};
</script>

<style lang="scss" scoped>
.confirm-dialog {
  max-height: 460px !important;
  min-height: 320px !important;
}
</style>
